import { Link } from "@inertiajs/react";
import { Ban, CheckCircle2, ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function ProspectsTable({ prospects, interestOptions, onUpdate }) {
  if (prospects.length === 0) {
    return (
      <Card className="p-10 text-center">
        <p className="font-medium text-foreground">No prospects found</p>
        <p className="text-sm text-muted-foreground mt-1">Try clearing one or more filters.</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1260px] text-sm">
          <thead className="bg-background border-b border-border">
            <tr className="text-xs uppercase tracking-wider text-muted-foreground">
              <th className="text-left px-4 py-3 font-medium">School</th>
              <th className="text-left px-4 py-3 font-medium">Students</th>
              <th className="text-left px-4 py-3 font-medium">School contact</th>
              <th className="text-left px-4 py-3 font-medium">Parent council</th>
              <th className="text-left px-4 py-3 font-medium">Contact</th>
              <th className="text-left px-4 py-3 font-medium">Interest</th>
              <th className="w-10"><span className="sr-only">Details</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {prospects.map((prospect) => (
              <tr key={prospect.id} className="hover:bg-secondary/30 transition-colors">
                <td className="px-4 py-3 max-w-[300px]">
                  <Link href={prospect.show_path} className="font-medium text-foreground hover:text-primary">{prospect.name}</Link>
                  <p className="text-xs text-muted-foreground truncate">
                    {prospect.province} · {prospect.division}{prospect.grade_range ? ` · Grades ${prospect.grade_range}` : ""}
                  </p>
                </td>
                <td className="px-4 py-3 font-mono tabular-nums text-muted-foreground">
                  {prospect.enrollment == null ? "—" : prospect.enrollment.toLocaleString()}
                </td>
                <td className="px-4 py-3 max-w-[250px]"><ContactCell contact={prospect.school_contact} emptyLabel="No school contact" /></td>
                <td className="px-4 py-3 max-w-[250px]"><ContactCell contact={prospect.parent_council_contact} emptyLabel="No parent council found" /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {prospect.do_not_contact ? (
                      <DoNotContactToggle prospect={prospect} onUpdate={onUpdate} />
                    ) : prospect.outreach_mailto && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={prospect.outreach_mailto} aria-label={`Draft outreach email to ${prospect.name}`}><Mail className="h-4 w-4" /> Email</a>
                      </Button>
                    )}
                    <Button type="button" size="sm" variant={prospect.contacted ? "secondary" : "outline"} onClick={() => onUpdate(prospect, { contacted: !prospect.contacted })} aria-pressed={prospect.contacted}>
                      {prospect.contacted && <CheckCircle2 className="h-4 w-4 text-success" />}
                      {prospect.contacted ? "Contacted" : "Mark contacted"}
                    </Button>
                    {!prospect.do_not_contact && <DoNotContactToggle prospect={prospect} onUpdate={onUpdate} />}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select value={prospect.interest_status} onChange={(event) => onUpdate(prospect, { interest_status: event.target.value })} aria-label={`Interest status for ${prospect.name}`} className="h-9 rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {interestOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </td>
                <td className="pr-4 py-3 text-right">
                  <Link href={prospect.show_path} className="inline-flex p-2 text-muted-foreground hover:text-primary" aria-label={`View ${prospect.name}`}><ExternalLink className="h-4 w-4" /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ContactCell({ contact, emptyLabel }) {
  if (!contact) return <span className="text-muted-foreground">{emptyLabel}</span>;

  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{contact.label}</p>
      {contact.name && <p className="text-xs text-foreground truncate">{contact.name}</p>}
      {contact.href ? (
        <a href={contact.href} target={contact.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-primary hover:underline truncate block">{contact.value}</a>
      ) : (
        <p className="text-foreground truncate">{contact.value}</p>
      )}
    </div>
  );
}

function DoNotContactToggle({ prospect, onUpdate }) {
  const toggle = () => onUpdate(prospect, { do_not_contact: !prospect.do_not_contact });

  if (prospect.do_not_contact) {
    return (
      <Button type="button" size="sm" variant="outline" onClick={toggle} aria-pressed="true" title="Click to allow contact again" className="text-destructive border-destructive/30 hover:bg-destructive/10">
        <Ban className="h-4 w-4" /> Do not contact
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" variant="ghost" onClick={toggle} aria-pressed="false" aria-label={`Mark ${prospect.name} do not contact`} title="Mark do not contact" className="px-2 text-muted-foreground hover:text-destructive">
      <Ban className="h-4 w-4" />
    </Button>
  );
}
